import {
  normalizeDeviceTrustMode,
  resolveDefaultDeviceTrustMode,
  type DeviceTrustMode,
} from './deviceTrust';

/** Default idle sign-out delay (minutes). */
export const DEFAULT_SESSION_IDLE_MINUTES = 30;
export const MIN_SESSION_IDLE_MINUTES = 5;
export const MAX_SESSION_IDLE_MINUTES = 120;

export const PLATFORM_FEATURE_FLAG_IDS = [
  'night_window',
  'parle',
  'centena',
  'auto_schedule',
  'runner_map',
  'charada_english',
  'maintenance_mode',
  'dev_panel',
] as const;

export type PlatformFeatureFlagId = typeof PLATFORM_FEATURE_FLAG_IDS[number];

export const OAUTH_PROVIDER_IDS = ['google', 'apple', 'facebook', 'microsoft'] as const;
export type OAuthProviderId = typeof OAUTH_PROVIDER_IDS[number];

export const MFA_METHOD_IDS = ['totp', 'sms', 'email'] as const;
export type MfaMethodId = typeof MFA_METHOD_IDS[number];

export interface PlatformConfig {
  sessionIdleMinutes: number;
  featureFlags: Record<PlatformFeatureFlagId, boolean>;
  authProviders: Record<OAuthProviderId, boolean>;
  mfaMethods: Record<MfaMethodId, boolean>;
  biometricLoginEnabled: boolean;
  deviceTrustMode: DeviceTrustMode;
  staffGoogleLinkOnboarding: boolean;
}

/**
 * Static defaults for missing docs. `dev_panel` defaults OFF; local hosts flip it on
 * via `createDefaultPlatformConfig(..., { devPanel: defaultDevPanelFlag() })`.
 */
export const DEFAULT_PLATFORM_FEATURE_FLAGS: Record<PlatformFeatureFlagId, boolean> = {
  night_window: true,
  parle: true,
  centena: true,
  auto_schedule: true,
  runner_map: true,
  charada_english: true,
  maintenance_mode: false,
  dev_panel: false,
};

export const DEFAULT_AUTH_PROVIDERS: Record<OAuthProviderId, boolean> = {
  google: true,
  apple: false,
  facebook: false,
  microsoft: false,
};

export const DEFAULT_MFA_METHODS: Record<MfaMethodId, boolean> = {
  totp: true,
  sms: false,
  email: false,
};

export function createDefaultPlatformConfig(
  emulatorMode = false,
  options?: { devPanel?: boolean },
): PlatformConfig {
  const featureFlags = { ...DEFAULT_PLATFORM_FEATURE_FLAGS };
  if (typeof options?.devPanel === 'boolean') {
    featureFlags.dev_panel = options.devPanel;
  }
  return {
    sessionIdleMinutes: DEFAULT_SESSION_IDLE_MINUTES,
    featureFlags,
    authProviders: { ...DEFAULT_AUTH_PROVIDERS },
    mfaMethods: { ...DEFAULT_MFA_METHODS },
    biometricLoginEnabled: false,
    // UI-only fallback; server decision is authoritative for enforcement.
    deviceTrustMode: resolveDefaultDeviceTrustMode(emulatorMode),
    staffGoogleLinkOnboarding: true,
  };
}

/** @deprecated Use createDefaultPlatformConfig() factory instead */
export const DEFAULT_PLATFORM_CONFIG: PlatformConfig = createDefaultPlatformConfig();

export function sessionIdleMinutesToMs(minutes: number): number {
  return minutes * 60 * 1000;
}

export function clampSessionIdleMinutes(minutes: number): number {
  if (!Number.isFinite(minutes)) return DEFAULT_SESSION_IDLE_MINUTES;
  return Math.min(MAX_SESSION_IDLE_MINUTES, Math.max(MIN_SESSION_IDLE_MINUTES, Math.round(minutes)));
}

function normalizeAuthProviders(raw: unknown): Record<OAuthProviderId, boolean> {
  const providers = { ...DEFAULT_AUTH_PROVIDERS };
  if (!raw || typeof raw !== 'object') return providers;
  const doc = raw as Record<string, unknown>;
  OAUTH_PROVIDER_IDS.forEach((id) => {
    if (typeof doc[id] === 'boolean') providers[id] = doc[id];
  });
  return providers;
}

function normalizeMfaMethods(raw: unknown): Record<MfaMethodId, boolean> {
  const methods = { ...DEFAULT_MFA_METHODS };
  if (!raw || typeof raw !== 'object') return methods;
  const doc = raw as Record<string, unknown>;
  MFA_METHOD_IDS.forEach((id) => {
    if (typeof doc[id] === 'boolean') methods[id] = doc[id];
  });
  return methods;
}

export function normalizePlatformConfig(raw: unknown, emulatorMode = false): PlatformConfig {
  if (!raw || typeof raw !== 'object') {
    return createDefaultPlatformConfig(emulatorMode);
  }
  const doc = raw as Record<string, unknown>;
  const flagsRaw = doc.featureFlags && typeof doc.featureFlags === 'object'
    ? doc.featureFlags as Record<string, unknown>
    : {};

  const featureFlags = { ...DEFAULT_PLATFORM_FEATURE_FLAGS };
  PLATFORM_FEATURE_FLAG_IDS.forEach((id) => {
    if (typeof flagsRaw[id] === 'boolean') featureFlags[id] = flagsRaw[id];
  });

  return {
    sessionIdleMinutes: clampSessionIdleMinutes(Number(doc.sessionIdleMinutes)),
    featureFlags,
    authProviders: normalizeAuthProviders(doc.authProviders),
    mfaMethods: normalizeMfaMethods(doc.mfaMethods),
    biometricLoginEnabled: doc.biometricLoginEnabled === true,
    deviceTrustMode: normalizeDeviceTrustMode(doc.deviceTrustMode, emulatorMode),
    staffGoogleLinkOnboarding: doc.staffGoogleLinkOnboarding !== false,
  };
}

export function canEditPlatformConfig(role: string | undefined): boolean {
  return role === 'god';
}

export function isOAuthProviderEnabled(
  config: PlatformConfig,
  providerId: OAuthProviderId,
): boolean {
  return Boolean(config.authProviders[providerId]);
}

export function isMfaMethodEnabled(config: PlatformConfig, methodId: MfaMethodId): boolean {
  return Boolean(config.mfaMethods[methodId]);
}
